import { useEffect, useRef } from 'react';
import {
  IDLE_ACTIVITY_STORAGE_KEY,
  IDLE_ACTIVITY_WRITE_THROTTLE_MS,
  IDLE_CHECK_INTERVAL_MS,
  IDLE_WARNING_MINUTES,
  idleTimeoutMs,
} from '@/constants/sessionIdle';
import { clock } from '@/utils/clock';

/**
 * Deliberate user interaction only — these and nothing else reset the timer:
 *   pointerdown / mousedown   a press (mouse, pen, touch; includes grabbing a scrollbar)
 *   keydown                   a key (includes keyboard scrolling and shortcuts)
 *   touchstart                a touch (includes starting a touch scroll / swipe)
 *   wheel                     a mouse-wheel or trackpad scroll — always user-initiated
 *   focus (window only)       the person returning to this browser window
 *   popstate                  browser back / forward navigation
 * NOT counted: mousemove / pointermove (hovering), the generic `scroll` event
 * (code can scroll), element focus (code can focus — dialogs autofocus),
 * visibility changes (checked, not counted), network, realtime, token
 * refresh, polling, timers and renders — none of which reach these listeners.
 */
export const IDLE_ACTIVITY_EVENTS = ['pointerdown', 'mousedown', 'keydown', 'touchstart', 'wheel', 'focus', 'popstate'] as const;

export interface StoredActivity { at: number; sid: string }

/** The shared record, or null when absent, unreadable or for another session. */
export function readStoredActivity(storage: Pick<Storage, 'getItem'>, sid: string): StoredActivity | null {
  try {
    const raw = storage.getItem(IDLE_ACTIVITY_STORAGE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<StoredActivity>;
    if (typeof v?.at !== 'number' || !Number.isFinite(v.at) || v.sid !== sid) return null;
    return { at: v.at, sid };
  } catch {
    return null;
  }
}

/**
 * Last activity as seen by this tab: the newer of the shared record and its
 * own memory. A timestamp in the future (clock moved back) counts as now —
 * never as idle time, and never as a reason to sign out.
 */
export function lastActivityAt(stored: StoredActivity | null, local: number | null, now: number): number | null {
  const candidates = [stored?.at, local].filter((v): v is number => typeof v === 'number');
  if (!candidates.length) return null;
  return Math.min(Math.max(...candidates), now);
}

export type IdleVerdict = 'ACTIVE' | 'WARN' | 'EXPIRED';
export function idleVerdict(last: number, now: number, timeoutMs: number, warningMs: number): IdleVerdict {
  const idle = now - last;
  if (idle >= timeoutMs) return 'EXPIRED';
  if (idle >= timeoutMs - warningMs) return 'WARN';
  return 'ACTIVE';
}

/**
 * The auth session a timestamp belongs to: the access token's `session_id`
 * claim (stable across token refreshes, new on every sign-in), else the user.
 * A reload of the same session therefore honours the stored inactivity; a
 * NEW sign-in starts from a fresh baseline.
 */
export function authSessionKey(accessToken: string | null | undefined, userId: string): string {
  try {
    const part = accessToken?.split('.')[1];
    if (part) {
      const json = atob(part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '='));
      const sid = (JSON.parse(json) as { session_id?: unknown }).session_id;
      if (typeof sid === 'string' && sid) return sid;
    }
  } catch {
    // fall through
  }
  return `user:${userId}`;
}

export interface IdleSignOutOptions {
  /** null while signed out: nothing runs. */
  sessionKey: string | null;
  /** Ends the session (all tabs converge through the auth layer). Must be safe to call twice. */
  signOut: () => Promise<void> | void;
  /** Shown once per approach to the limit; `dismiss` when activity returns. */
  warn?: { show: (msLeft: number) => void; dismiss: () => void };
  timeoutMs?: number;
  storage?: Storage;
  target?: Window;
}

/**
 * Signs this browser out after `timeoutMs` without real activity in ANY tab.
 * Every tab shares one activity record (localStorage), so a user working in
 * one tab keeps an idle sibling tab signed in, and when all are idle each tab
 * reaches the same verdict and the session ends once.
 */
export function useIdleSignOut({
  sessionKey, signOut, warn, timeoutMs = idleTimeoutMs(), storage, target,
}: IdleSignOutOptions): void {
  const signOutRef = useRef(signOut);
  signOutRef.current = signOut;
  const warnRef = useRef(warn);
  warnRef.current = warn;

  useEffect(() => {
    if (!sessionKey) return;
    const store = storage ?? (typeof window !== 'undefined' ? window.localStorage : undefined);
    const win = target ?? (typeof window !== 'undefined' ? window : undefined);
    if (!store || !win) return;
    const warningMs = IDLE_WARNING_MINUTES * 60_000;
    let local: number | null = null;
    let lastWrite = 0;
    let warned = false;
    let signingOut = false;

    const write = (at: number) => {
      try { store.setItem(IDLE_ACTIVITY_STORAGE_KEY, JSON.stringify({ at, sid: sessionKey })); } catch { /* storage unavailable */ }
      lastWrite = at;
    };
    const expire = () => {
      if (signingOut) return;
      signingOut = true;
      warnRef.current?.dismiss();
      void Promise.resolve(signOutRef.current()).finally(() => { signingOut = false; });
    };
    /** Evaluates; returns false once the session is past the limit. */
    const check = (): boolean => {
      const now = clock.nowMs();
      const stored = readStoredActivity(store, sessionKey);
      const last = lastActivityAt(stored, local, now);
      if (last === null) { local = now; write(now); return true; }   // fail safe: no data is never "idle"
      if (stored && stored.at > now) write(now);                      // clock moved back: re-anchor
      const verdict = idleVerdict(last, now, timeoutMs, warningMs);
      if (verdict === 'EXPIRED') { expire(); return false; }
      if (verdict === 'WARN' && !warned) { warned = true; warnRef.current?.show(timeoutMs - (now - last)); }
      if (verdict === 'ACTIVE' && warned) { warned = false; warnRef.current?.dismiss(); }
      return true;
    };
    const onActivity = (ev?: Event) => {
      // Window focus only: a capturing listener also hears every element focus,
      // and code focuses elements (a dialog's autofocus) without anyone there.
      if (ev?.type === 'focus' && typeof Node !== 'undefined' && ev.target instanceof Node) return;
      // Past the limit, activity does not revive the session (a laptop waking
      // after hours, a click on a stale tab): the check signs out first.
      if (!check()) return;
      const now = clock.nowMs();
      local = now;
      if (warned) { warned = false; warnRef.current?.dismiss(); }
      if (now - lastWrite >= IDLE_ACTIVITY_WRITE_THROTTLE_MS) write(now);
    };
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };

    // Fresh baseline for a session this browser has no record of (a new
    // sign-in); a reload of a known session keeps — and enforces — its record.
    if (!readStoredActivity(store, sessionKey)) { local = clock.nowMs(); write(local); }
    check();

    const opts: AddEventListenerOptions = { passive: true, capture: true };
    for (const ev of IDLE_ACTIVITY_EVENTS) win.addEventListener(ev, onActivity, opts);
    document.addEventListener('visibilitychange', onVisible);
    const timer = setInterval(check, IDLE_CHECK_INTERVAL_MS);
    return () => {
      clearInterval(timer);
      for (const ev of IDLE_ACTIVITY_EVENTS) win.removeEventListener(ev, onActivity, opts);
      document.removeEventListener('visibilitychange', onVisible);
      if (warned) warnRef.current?.dismiss();
    };
  }, [sessionKey, timeoutMs, storage, target]);
}
